import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { PROJECTS } from "@/data/content";
import { useReveal } from "@/hooks/useReveal";

const EASE = [0.2, 0.8, 0.2, 1];

function Lightbox({ project, index, onClose }) {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(0);
  const gallery = project.gallery || [project.image];

  const go = useCallback(
    (d) => {
      setDir(d);
      setI((prev) => (prev + d + gallery.length) % gallery.length);
    },
    [gallery.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);

  return createPortal(
    <div className="site">
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
        data-testid="project-lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
      {/* backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(6,4,4,0.82)", backdropFilter: "blur(16px)" }}
        onClick={onClose}
        data-testid="lightbox-backdrop"
      />
      <div className="glow-field" style={{ opacity: 0.5, pointerEvents: "none" }} />

      {/* close */}
      <motion.button
        data-testid="lightbox-close"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 0.25 } }}
        exit={{ opacity: 0 }}
        className="absolute top-5 right-5 z-20 grid place-items-center h-11 w-11 rounded-full nav-pill text-[var(--ink)] hover:bg-[var(--accent)] transition-colors"
      >
        <X className="h-5 w-5" />
      </motion.button>

      {/* panel */}
      <div className="relative z-10 w-full max-w-[1200px] grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-0 lg:gap-8 items-center">
        {/* media frame (shared-element morph) */}
        <motion.div
          layoutId={`frame-${index}`}
          className="relative overflow-hidden rounded-xl border border-[var(--line-strong)] bg-black"
          style={{ aspectRatio: "1 / 1" }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          <AnimatePresence initial={false} custom={dir} mode="popLayout">
            <motion.img
              key={i}
              src={gallery[i]}
              alt={`${project.title} ${i + 1}`}
              custom={dir}
              initial={{ opacity: 0, x: dir >= 0 ? 60 : -60, scale: 1.04 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: dir >= 0 ? -60 : 60, scale: 1.04 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>

          {/* nav arrows */}
          {gallery.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={() => go(-1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full nav-pill text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[#0a0707] transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={() => go(1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 grid place-items-center h-11 w-11 rounded-full nav-pill text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[#0a0707] transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 chip rounded-full px-3 py-1 label !text-[0.6rem] !text-[var(--ink)]">
                {String(i + 1).padStart(2, "0")} / {String(gallery.length).padStart(2, "0")}
              </div>
            </>
          )}
        </motion.div>

        {/* info */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.5, ease: EASE } }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          className="px-1 lg:px-0 pt-6 lg:pt-0"
        >
          <div className="flex items-center justify-between">
            <span className="label !text-[var(--accent)]">{project.category}</span>
            <span className="font-mono text-xs text-[var(--ink-dim)]">{project.year}</span>
          </div>
          <h3 className="display text-3xl sm:text-[2.75rem] text-[var(--ink)] mt-4">
            {project.title}
          </h3>
          <p className="mt-5 text-[var(--ink-dim)] leading-relaxed max-w-md">
            {project.summary}
          </p>

          {/* thumbnails */}
          {gallery.length > 1 && (
            <div className="mt-7 flex gap-3">
              {gallery.map((g, gi) => (
                <button
                  key={gi}
                  data-testid={`lightbox-thumb-${gi}`}
                  onClick={() => {
                    setDir(gi > i ? 1 : -1);
                    setI(gi);
                  }}
                  className="relative h-14 w-20 rounded-md overflow-hidden border transition-all duration-300"
                  style={{
                    borderColor: gi === i ? "var(--accent)" : "var(--line)",
                    opacity: gi === i ? 1 : 0.5,
                  }}
                >
                  <img src={g} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
    </div>,
    document.body
  );
}

function ProjectCard({ p, i, onOpen }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });
  // per-column stagger so a row flips left-to-right, one by one
  const col = i % 3;
  const a = col * 0.13;
  const rotateX = useTransform(scrollYProgress, [a, a + 0.55], [62, 0]);
  const y = useTransform(scrollYProgress, [a, a + 0.55], [60, 0]);
  const opacity = useTransform(scrollYProgress, [a, a + 0.4], [0, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ rotateX, y, opacity, transformPerspective: 1400, transformOrigin: "center top" }}
      className="w-full relative"
    >
      <motion.button
        type="button"
        data-testid={`project-${i}`}
        onClick={() => onOpen(i)}
        layoutId={`frame-${i}`}
        className="project-card rounded-xl group block text-left w-full"
        style={{ aspectRatio: i % 5 === 0 ? "4 / 5" : "4 / 3" }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        <img
          src={p.image}
          alt={p.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="pc-overlay absolute inset-0" />
        <div className="absolute inset-0 p-5 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="label !text-[var(--ink-dim)]">{p.category}</span>
            <span className="grid place-items-center h-8 w-8 rounded-full bg-[var(--ink)] text-[#0a0707] opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
          <div className="flex items-end justify-between">
            <h3 className="display text-2xl sm:text-[1.7rem] text-[var(--ink)]">{p.title}</h3>
            <span className="font-mono text-xs text-[var(--ink-dim)]">{p.year}</span>
          </div>
        </div>
      </motion.button>
    </motion.div>
  );
}

export default function Projects() {
  const ref = useReveal();
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      ref={ref}
      data-testid="projects-section"
      className="relative py-28 sm:py-36 bg-[var(--bg-soft)] border-y border-[var(--line)]"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 reveal">
          <div>
            <span className="label">Selected work</span>
            <h2 className="display mt-5 text-[2.25rem] sm:text-[3.25rem] text-[var(--ink)]">
              Featured <span className="serif-italic">projects.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" style={{ perspective: "1400px" }}>
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.title} p={p} i={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <Lightbox
            project={PROJECTS[active]}
            index={active}
            onClose={() => setActive(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
